import { useCallback, useMemo } from "react";
import { SharedValue } from "react-native-reanimated";
import { INLINE_CREATE_ALBUM } from "src/api/featureFlags";
import CollectDataField from "src/api/graphql/__generated__/enums/CollectDataField";
import { useStickerRowItemAllocation } from "src/components/StickerRow";
import { useGatheringStore } from "src/contexts/GatheringContext";
import parseStickerTemplateUrl from "src/helpers/parseV0TemplateUrl/parseStickerTemplateUrl";
import useDim from "src/hooks/useDim";
import { GatheringOrLocalPhoto } from "src/hooks/useGatheringPhotosWithLocal";
import { ListRenderItemInfo } from "src/swsh-native";
import FilterNoPhotos from "../components/FilterNoPhotos";
import FullHeightElementWrapper from "../components/FullHeightElementWrapper";
import GatheringHomeHeader from "../components/GatheringHomeHeader";
import GatheringHomeHeaderSpacer from "../components/GatheringHomeHeaderSpacer";
import GatheringHomeRow from "../components/GatheringHomeRow";
import NoPhotosHidden from "../components/NoPhotosHidden";
import NoPhotosUpload from "../components/NoPhotosUpload";
import { GatheringHomeItemType, GatheringHomeRowItemType } from "../types";
import useGatheringHomeIsEmpty from "./useGatheringHomeIsEmpty";
import useGatheringHomePhotoSpacing from "./useGatheringHomePhotoSpacing";
import useShouldCreateGatheringPrompt from "./useShouldCreateGatheringPrompt";

const useGatheringHomeComponentData = ({
	numColumns,
	photos,
	topPhotos,
	loading,
	refreshing,
	scrollOffset,
}: {
	numColumns: number;
	photos: GatheringOrLocalPhoto[];
	topPhotos: GatheringOrLocalPhoto[];
	loading: boolean;
	refreshing: boolean;
	scrollOffset: SharedValue<number>;
}) => {
	const dim = useDim();

	const viewMode = useGatheringStore((state) => state.viewMode);
	const hasFilter = useGatheringStore(
		(state) =>
			state.filterUserIds.length > 0 ||
			state.filterPhotoVariant.length > 0 ||
			state.filterUploaderIds.length > 0 ||
			state.filterLabels.length > 0 ||
			state.filterRestrictedLabels.length > 0,
	);
	const { isEmpty } = useGatheringHomeIsEmpty({
		loading,
		refreshing,
		photos,
	});

	const shouldCreateGatheringPrompt = useShouldCreateGatheringPrompt();
	const { spacing, containerHorizontalPadding } = useGatheringHomePhotoSpacing();
	const stickerPack = useGatheringStore((state) => state.stickerPack);
	const collectData = useGatheringStore((state) => state.collectData);
	const previewPhotos = useGatheringStore((state) => state.previewPhotos);

	const { getParsedSticker } = useStickerRowItemAllocation({
		numColumns,
		padding: spacing,
		stickers: useMemo(
			() =>
				(stickerPack?.stickers ?? []).map((sticker) => ({
					url: parseStickerTemplateUrl({ readUrl: sticker.readUrl }),
				})),
			[stickerPack],
		),
	});
	const width = useMemo(() => {
		return (
			(dim.width - 2 * containerHorizontalPadding - spacing * (numColumns - 1)) / numColumns
		);
	}, [containerHorizontalPadding, dim.width, numColumns, spacing]);

	const gatheringHomeItems = useMemo(() => {
		const items: GatheringHomeItemType[] = [];
		if (viewMode === "AllCaller" || viewMode === "Hidden") {
			items.push({
				variant: "HeaderSpacer",
				itemKey: "HeaderSpacer",
			});
		} else {
			items.push({
				variant: "Header",
				itemKey: "Header",
			});
		}

		const curPhotos = viewMode === "Top" ? topPhotos : photos;
		const shouldPromptCreateAlbum =
			INLINE_CREATE_ALBUM &&
			shouldCreateGatheringPrompt &&
			curPhotos.length !== 0 &&
			// LinkedIn albums shouldn't have the prompt card
			!(collectData ?? []).includes(CollectDataField.LinkedIn);
		if (!isEmpty) {
			let iPhoto = 0;
			const numItems = curPhotos.length + +shouldPromptCreateAlbum;
			let didAddCreateAlbum = false;
			const createPromptIndex = numColumns > 2 ? numColumns - 2 : numColumns - 1;
			for (let i = 0; i < numItems; i += numColumns) {
				const row: GatheringHomeRowItemType = {
					variant: "Row",
					data: [],
					itemKey: "temp",
				};
				for (let j = 0; j < numColumns; j++) {
					if (
						shouldPromptCreateAlbum &&
						!didAddCreateAlbum &&
						((i === 2 * numColumns && j === createPromptIndex) ||
							iPhoto >= curPhotos.length)
					) {
						didAddCreateAlbum = true;
						row.data.push({
							variant: "CreateAlbum",
							itemKey: `CreateAlbum`,
						});
						continue;
					}
					if (iPhoto < curPhotos.length) {
						const photo = curPhotos[iPhoto++];
						if (!photo) continue;
						row.data.push({
							variant: "Photo",
							itemKey: photo.photoId,
							photo,
							sticker: getParsedSticker({
								rowIndex: i / numColumns,
								columnIndex: j,
							}),
						});
						continue;
					}
					row.data.push({
						variant: "Empty",
						itemKey: `Empty-${i}-${j}`,
					});
				}
				row.itemKey = row.data.map((r) => r.itemKey).join("-");
				items.push(row);
			}
		}
		if (isEmpty) {
			if (hasFilter) {
				items.push({
					variant: "EmptyNoPhotosFilter",
					itemKey: "EmptyNoPhotosFilter",
				});
			} else if (viewMode === "Hidden") {
				items.push({
					variant: "EmptyHidden",
					itemKey: "EmptyHidden",
				});
			} else {
				items.push({
					variant: "EmptyNoPhotos",
					itemKey: "EmptyNoPhotos",
				});
			}
		}

		return items;
	}, [
		collectData,
		getParsedSticker,
		hasFilter,
		isEmpty,
		numColumns,
		photos,
		shouldCreateGatheringPrompt,
		topPhotos,
		viewMode,
	]);

	const renderItem = useCallback(
		({ item, target }: ListRenderItemInfo<GatheringHomeItemType>) => {
			const { variant } = item;
			switch (variant) {
				case "Header":
					return (
						<GatheringHomeHeader
							target={target}
							scrollOffset={scrollOffset}
							previewPhotos={previewPhotos}
						/>
					);
				case "HeaderSpacer":
					return <GatheringHomeHeaderSpacer />;
				case "Row":
					return <GatheringHomeRow row={item} itemWidth={width} target={target} />;
				case "EmptyNoPhotos":
					return (
						<FullHeightElementWrapper
							renderChildren={({ height }) => <NoPhotosUpload height={height} />}
						/>
					);
				case "EmptyHidden":
					return (
						<FullHeightElementWrapper>
							<NoPhotosHidden />
						</FullHeightElementWrapper>
					);
				case "EmptyNoPhotosFilter":
					return (
						<FullHeightElementWrapper>
							<FilterNoPhotos />
						</FullHeightElementWrapper>
					);
				default:
					return null;
			}
		},
		[previewPhotos, scrollOffset, width],
	);

	return {
		gatheringHomeItems,
		renderItem,
		isEmpty,
	};
};
export default useGatheringHomeComponentData;
